/**
 * The operator overview, in one request.
 *
 * Mirrors apps/api/src/modules/crm-dashboard/types.ts. Aggregating on the
 * server is not an optimisation here: every CRM list route caps `pageSize` at
 * 100, so a dashboard built out of list calls would chart the newest hundred
 * invoices and present it as the year.
 */
import { api } from "./api";
import type { CrmGoalSet } from "./goals";

export type DashPeriod = "month" | "quarter" | "year";

export interface DashKpi {
  revenueCents: number;
  revenueDeltaPct: number | null;
  leadsNew: number;
  leadsNewDeltaPct: number | null;
  dealsWon: number;
  dealsWonDeltaPct: number | null;
  outstandingCents: number;
  outstandingCount: number;
  overdueCents: number;
  overdueCount: number;
}

export interface DashMonth {
  month: string;
  invoicedCents: number;
  collectedCents: number;
}

export interface DashQuick {
  openTickets: number;
  urgentTickets: number;
  /** Calls WE placed today - outbound only, so switching the inbound line on
   *  does not silently double the desk's own productivity number. */
  callsToday: number;
  callSecondsToday: number;
  /** How many of those were picked up at the far end. The pair is the operator's
   *  answer rate, which is not visible anywhere else. */
  connectedToday: number;
  conversionPct: number;
  activeProjects: number;
  /** Live leads with nothing planned ahead of them - including the ones whose
   *  only planned step is already late. Both are records nobody has a date for. */
  leadsNoNextStep: number;
}

export interface DashFunnelStage {
  status: string;
  count: number;
  valueCents: number;
}

export interface DashSource {
  source: string;
  count: number;
}

export interface DashCampaign {
  id: string;
  name: string;
  status: string;
  audience: string;
  sent: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
  at: string | null;
}

export interface DashAgendaItem {
  key: string;
  at: string | null;
  kind: "task" | "contract" | "campaign" | "ticket";
  title: string;
  sub: string;
  to: string;
}

export interface DashActivityDay {
  day: string;
  calls: number;
  mails: number;
  tasks: number;
}

export interface DashAttentionItem {
  key: string;
  kind: "invoice" | "ticket" | "contract" | "estimate";
  name: string;
  what: string;
  days: number | null;
  tone: "ok" | "wt" | "lt";
  to: string;
}

export interface DashInvoice {
  id: string;
  number: string;
  clientName: string;
  clientEmail: string | null;
  issueDate: string;
  totalCents: number;
  status: string;
}

export interface DashFeedItem {
  key: string;
  at: string;
  kind: "invoice_paid" | "lead" | "campaign" | "call" | "ticket" | "contract";
  title: string;
  detail: string;
}

export interface DashboardPayload {
  period: DashPeriod;
  kpi: DashKpi;
  /**
   * The targets for the SELECTED period, already cut by the server.
   *
   * Every field is nullable and a null one draws exactly as the tile did before
   * targets existed, so a CRM where nobody has set a goal looks unchanged.
   */
  goals: CrmGoalSet;
  series: DashMonth[];
  quick: DashQuick;
  funnel: DashFunnelStage[];
  sources: DashSource[];
  campaigns: DashCampaign[];
  agenda: DashAgendaItem[];
  activity: DashActivityDay[];
  attention: DashAttentionItem[];
  invoices: DashInvoice[];
  feed: DashFeedItem[];
}

export const getDashboard = (period: DashPeriod) =>
  api.get<DashboardPayload>(`/admin/crm/dashboard?period=${period}`);
